import React, { useMemo, useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Sun } from 'lucide-react';
import { ToastContainer } from 'react-toastify';
import logoImage from '/planifai-last-sinfondo.webp';

const TITLE_LETTERS = "Planif".split("");

const LETTER_ANIMATION = {
    hidden: { opacity: 0, y: 20 },
    visible: (i: number) => ({
        opacity: 1,
        y: 0,
        transition: {
            delay: i * 0.05,
        },
    }),
};

const AI_ANIMATION = {
    hidden: { opacity: 0, scale: 0.5 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: {
            delay: 0.3,
            duration: 0.4,
            type: "spring",
            stiffness: 200,
        }
    }
};

const SUN_ANIMATION = {
    animate: {
        scale: [1, 1.2, 1],
        rotate: [0, 5, -5, 0]
    },
    transition: {
        duration: 2,
        repeat: Infinity,
        repeatType: "reverse"
    }
};

// Preload image function
const preloadImage = (src: string): Promise<void> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = reject;
        img.src = src;
    });
};

export const PublicLayout: React.FC = () => {
    const { t } = useTranslation();
    const [imageLoaded, setImageLoaded] = useState(false);

    const logoPath = logoImage;

    useEffect(() => {
        preloadImage(logoPath)
            .then(() => setImageLoaded(true))
            .catch(err => console.error("Failed to load image:", err));

        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = logoPath;
        document.head.appendChild(link);

        return () => {
            document.head.removeChild(link);
        };
    }, [logoPath]);

    const titleLetterElements = useMemo(() => (
        TITLE_LETTERS.map((letter, i) => (
            <motion.span
                key={i}
                custom={i}
                initial="hidden"
                animate="visible"
                variants={LETTER_ANIMATION}
                className="text-3xl font-bold text-white tracking-tight"
            >
                {letter}
            </motion.span>
        ))
    ), []);

    return (
        <div className="min-h-screen bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900 via-gray-900 to-black flex items-center justify-center p-4">
            <div className="relative w-full max-w-md">
                {/* Background elements */}
                <div className="absolute -top-4 -left-4 w-24 h-24 bg-blue-500/10 rounded-full blur-xl" />
                <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-purple-500/10 rounded-full blur-xl" />

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="relative bg-white/10 backdrop-blur-xl p-8 rounded-2xl shadow-2xl border border-white/20 overflow-hidden"
                >
                    {/* Gradient overlay */}
                    <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b from-white/10 to-transparent pointer-events-none" />

                    <div className="relative">
                        {/* Logo section with optimized image loading */}
                        <div className="flex justify-center mb-6">
                            <div className="relative">
                                <div className="w-32 h-32 relative">
                                    <img
                                        src={logoPath}
                                        alt="PlanifAI Logo"
                                        className={`w-32 h-32 object-contain drop-shadow-lg absolute inset-0 transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                                        loading="eager"
                                        decoding="async"
                                        fetchPriority='high'
                                        onLoad={() => setImageLoaded(true)}
                                    />
                                </div>
                                <motion.div
                                    {...SUN_ANIMATION}
                                    className="absolute -top-2 -right-2"
                                >
                                    <Sun className="w-6 h-6 text-yellow-400" />
                                </motion.div>
                            </div>
                        </div>

                        {/* Title section */}
                        <div className="flex justify-center items-baseline mb-2">
                            {titleLetterElements}
                            <motion.span
                                initial="hidden"
                                animate="visible"
                                variants={AI_ANIMATION}
                                className="text-3xl font-bold tracking-tight"
                            >
                                <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">AI</span>
                            </motion.span>
                        </div>

                        {/* Tagline and content */}
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.2 }}
                            className="text-gray-300 text-center mb-8 text-sm"
                        >
                            {t('auth.tagline')}
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.2 }}
                            className="w-full"
                        >
                            <Outlet />
                            <ToastContainer />
                        </motion.div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};